/**
 * One person's several applications, folded into a single inbox row, so an
 * employer does not answer one of them without seeing the others. Rows are
 * ordered by recency, so a person's chats are only ever adjacent by luck.
 */
import type { ChannelId, InboxItem, ParticipantId } from '../fixtures/domain.js';
import type { Participant } from '../fixtures/domain.js';
import { isEnded } from '../fixtures/rules.js';

/**
 * One line in the ungrouped list: a single conversation, or the several
 * applications one person has with this employer folded into one.
 */
export type InboxRow =
  | { kind: 'chat'; key: string; item: InboxItem }
  | { kind: 'stack'; key: string; counterpart: Participant; items: InboxItem[] };

/** Every application a person holds, keyed by them. See `useStackedApplications`. */
export type ApplicationSets = ReadonlyMap<ParticipantId, InboxItem[]>;

function byRecency(a: InboxItem, b: InboxItem): number {
  return Date.parse(b.channel.lastActivityAt) - Date.parse(a.channel.lastActivityAt);
}

/**
 * Live conversations only. The widget draws open and ending chats in one list
 * where /chat's query excludes ended ones, so the rule has to hold here on its
 * own. An ending chat keeps its own row: it carries a deletion countdown, and a
 * countdown behind a disclosure triangle is one nobody sees run out.
 */
function foldable(item: InboxItem): boolean {
  return !isEnded(item.channel);
}

/**
 * A person's applications, gathered out of a list that already holds all of
 * them. That is true of a saved list's detail view, whose members are resolved
 * in full against Stream rather than paged (`useListMembers`) — so it needs no
 * read-model round trip to know how many applications someone has, only this.
 *
 * The paginated inbox cannot use it: a page holds an arbitrary window of the
 * pile, and counting applications inside that window is how a row comes to
 * claim someone applied twice when they applied four times.
 */
export function groupByPerson(items: InboxItem[]): ApplicationSets {
  const sets = new Map<ParticipantId, InboxItem[]>();
  for (const item of items) {
    // See `foldable` for what does and does not belong in a row.
    if (!foldable(item)) continue;
    const held = sets.get(item.counterpart.id);
    if (held) held.push(item);
    else sets.set(item.counterpart.id, [item]);
  }
  return sets;
}

/**
 * Folded chats a row shows that the loaded pages do not hold. Select-all must
 * reach them, or a row whose box says "all four picked" hands one to an
 * irreversible bulk End. Gated on `foldable`, not `jobMeta`, so the box and the
 * action agree about what the row contains.
 */
export function foldedExtras(items: InboxItem[], applications: ApplicationSets): InboxItem[] {
  const loaded = new Set(items.map((item) => item.channel.id));
  const seen = new Set<ParticipantId>();
  const extras: InboxItem[] = [];
  for (const item of items) {
    const id = item.counterpart.id;
    if (!foldable(item) || seen.has(id)) continue;
    const set = applications.get(id);
    if (!set || set.length < 2) continue;
    seen.add(id);
    for (const app of set) if (!loaded.has(app.channel.id)) extras.push(app);
  }
  return extras;
}

/**
 * `items` is the column as the read model ordered it; `applications` says what
 * each person holds, which a paged inbox cannot answer on its own.
 *
 * Only live conversations stack (see `foldable`). Two or more, or nothing. The
 * row sits where its newest member sat, so a person does not move in the column
 * for having applied twice. An empty `applications` folds nothing, which makes
 * this safe to render before the membership answer arrives.
 */
export function stackRows(items: InboxItem[], applications: ApplicationSets): InboxRow[] {
  const rows: InboxRow[] = [];
  const folded = new Set<ParticipantId>();

  for (const item of items) {
    const id = item.counterpart.id;
    const set = foldable(item) ? applications.get(id) : undefined;
    if (!set || set.length < 2) {
      rows.push({ kind: 'chat', key: item.channel.id, item });
      continue;
    }
    // The second, third and fourth application this person holds. Their row is
    // already standing where the newest of them put it.
    if (folded.has(id)) continue;
    folded.add(id);
    rows.push({
      // Keyed off the person and not the channel: the row outlives whichever of
      // their chats happens to be newest, so React must not throw the expanded
      // state away when a reply reorders the applications inside it.
      key: `stack:${id}`,
      kind: 'stack',
      counterpart: item.counterpart,
      // Sorted here rather than trusted from the caller, because the rows inside
      // an open stack are read newest-first like every other list in this column
      // and there is no reason for two callers to have to know that.
      items: [...set].sort(byRecency),
    });
  }
  return rows;
}

/**
 * How many of a folded row's chats have something new in them.
 *
 * Chats, not messages: the row summarises conversations, and the threads that
 * open underneath mark themselves one by one. A message count up here would be
 * a number the fold cannot show the employer where to spend.
 */
export function unreadChats(items: InboxItem[]): number {
  return items.filter((item) => item.channel.unreadCount > 0).length;
}

/**
 * How many of a fold's chats the assistant would have the employer decide on.
 *
 * A count, though the header draws it as a single mark: the number is what the
 * header needs to know whether to draw anything at all, and callers that want
 * "any" ask for `> 0` rather than this growing a second boolean twin.
 */
export function flaggedChats(items: InboxItem[]): number {
  return items.filter((item) => item.channel.assistantFlag === true).length;
}

/** Every channel a set of rows puts on screen, stacked members included. */
export function rowChannelIds(rows: InboxRow[]): ChannelId[] {
  return rows.flatMap((row) =>
    row.kind === 'chat' ? [row.item.channel.id] : row.items.map((item) => item.channel.id),
  );
}
